import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { ModalProvider, useModal } from './context/ModalContext';
import toast from 'react-hot-toast';
import { BookOpen, AlertCircle } from 'lucide-react';
import { SearchBar } from './components/SearchBar';
import { AlphabeticalFilter } from './components/FilterControls';
import { SeriesList } from './components/SeriesList';
import { RecapCard } from './components/RecapCard';
import { RecapModal } from './components/RecapModal';
import { LoadingSpinner } from './components/LoadingSpinner';
import { ChannelHeader } from './components/ChannelHeader';
import { Navigation } from './components/Navigation';
import { ThemeToggle } from './components/ThemeToggle';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import { useDebounce } from './hooks/useDebounce';
import { useRecaps } from './hooks/useRecaps';
import { useLanguages } from './hooks/useLanguages';
import { useSearchHistory } from './hooks/useSearchHistory';
import { ApiService } from './services/api';
import { ManhwaRecap, FilterOptions } from './types';
import { profiler } from './utils/profiler';

function App() {
  const { channelId } = useParams<{ channelId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, logout, isAuthenticated, loading: authLoading, needsEmailVerification, verificationDaysLeft, canAccessContent } = useAuth();

  const navigate = useNavigate();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    await logout();
    toast.success('Logged out successfully.');
  };

  useEffect(() => {
    if (isLoggingOut && !isAuthenticated) {
      setIsLoggingOut(false);
      navigate('/');
    }
  }, [isLoggingOut, isAuthenticated, navigate]);



  // Initialize searchQuery from URL parameter if present
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  // Initialize filters - if series is in URL, don't set startsWith in filters
  const initialStartsWith = searchParams.get('series') ? undefined : (searchParams.get('startsWith') || undefined);
  const [filters, setFilters] = useState<FilterOptions>({
    language: searchParams.get('language') || 'en',
    sortBy: 'published_at',
    sortOrder: 'desc',
    searchQuery: '',
    startsWith: initialStartsWith
  });
  const [seriesList, setSeriesList] = useState<{ series_name: string; recap_count: number; series_thumbnail?: string }[]>([]);
  const [loadingSeries, setLoadingSeries] = useState(false);
  const [seriesPage, setSeriesPage] = useState(1);
  const [seriesHasMore, setSeriesHasMore] = useState(false);
  const [seriesTotal, setSeriesTotal] = useState(0);
  // Pagination state for alphabetical series list (A-Z)
  const [letterSeriesPage, setLetterSeriesPage] = useState(1);
  const [letterSeriesHasMore, setLetterSeriesHasMore] = useState(false);
  const [letterSeriesTotal, setLetterSeriesTotal] = useState(0);
  // Initialize selectedSeriesName from URL if present
  const [selectedSeriesName, setSelectedSeriesName] = useState<string | null>(
    searchParams.get('series') || null
  );
  // Initialize selectedGenre from URL if present
  const [selectedGenre, setSelectedGenre] = useState<string | null>(
    searchParams.get('genre') || null
  );
  const [showAlphabeticalFilter, setShowAlphabeticalFilter] = useState(false);

  // Helper function to determine selected duration value
  const getSelectedDurationValue = () => {
    if (filters.minDuration === undefined && filters.maxDuration === undefined) return '';
    if (filters.maxDuration === 3600) return 'under1h';
    if (filters.minDuration === 3600 && filters.maxDuration === 7200) return '1to2h';
    if (filters.minDuration === 7200 && filters.maxDuration === 14400) return '2to4h';
    if (filters.minDuration === 14400) return 'over4h';
    return '';
  };

  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const { languages } = useLanguages();
  const { saveSearchToHistory } = useSearchHistory();
  const lastSavedSearchRef = useRef<string>('');
  const searchTriggeredRef = useRef(false); // Track when Enter is pressed
  const clearingSeriesRef = useRef(false); // Track when we're clearing series selection
  const isSearchingRef = useRef(false); // Track if we're currently performing a search to prevent duplicates
  const goingHomeRef = useRef(false); // Track when we're going home to ensure fetch happens
  const lastSearchQueryRef = useRef<string>(''); // Track last search query to prevent duplicate fetches
  const syncingRef = useRef(false); // Track when we're syncing to prevent loops
  const fetchingRef = useRef(false); // Track when we're fetching to prevent concurrent fetches
  const lastSyncParamsRef = useRef<string>(''); // Track last sync params to prevent unnecessary syncs
  const lastFetchParamsRef = useRef<string>(''); // Track last fetch params to prevent duplicate fetches
  const lastFetchTimeRef = useRef<number>(0); // Track when last fetch started to detect rapid duplicates
  const lastGoHomeClickTimeRef = useRef<number>(0); // Track last logo click time to prevent double-clicks
  const isLoadingMoreSeriesRef = useRef(false); // Track when we're loading more series to prevent scroll-to-top

  const { recaps, loading, error, fetchRecaps, loadMore, hasMore, total, setRecaps } = useRecaps();

  // Update URL when language filter changes
  const lastLanguageRef = useRef<string | null>(filters.language);
  useEffect(() => {
    // Only update if language actually changed
    if (lastLanguageRef.current === filters.language) {
      return;
    }
    lastLanguageRef.current = filters.language;

    const currentUrlLanguage = searchParams.get('language');
    const newLanguage = filters.language && filters.language !== 'en' ? filters.language : null;

    // Only update URL if language actually changed (prevent loops)
    if (currentUrlLanguage !== newLanguage) {
      const newSearchParams = new URLSearchParams(searchParams);
      if (newLanguage) {
        newSearchParams.set('language', newLanguage);
      } else {
        newSearchParams.delete('language');
      }
      setSearchParams(newSearchParams);
    }
  }, [filters.language]); // Only depend on filters.language to prevent loops

  // Sync selectedSeriesName with URL parameter and clear startsWith filter when series is selected
  useEffect(() => {
    // Don't sync if we're actively clearing the series (to prevent re-adding it)
    if (clearingSeriesRef.current) {
      console.log('Sync useEffect: Skipping - series is being cleared');
      return;
    }

    // Don't sync if we're going home (handleGoHome handles state clearing)
    if (goingHomeRef.current) {
      console.log('Sync useEffect: Skipping - going home');
      return;
    }

    // Prevent infinite loops
    if (syncingRef.current) {
      console.log('Sync useEffect: Skipping - already syncing');
      return;
    }

    // Use React Router's searchParams as source of truth (it updates synchronously)
    // window.location.search might be stale during navigation
    const urlSeries = searchParams.get('series');
    const urlStartsWith = searchParams.get('startsWith');

    // Create a key from URL params to check if we've already synced this state
    const urlGenre = searchParams.get('genre');
    const currentParamsKey = `${urlSeries || ''}|${urlStartsWith || ''}|${urlGenre || ''}`;
    if (lastSyncParamsRef.current === currentParamsKey) {
      console.log('Sync useEffect: Skipping - already synced these params');
      return;
    }
    lastSyncParamsRef.current = currentParamsKey;

    console.log('Sync useEffect:', {
      urlSeries,
      urlStartsWith,
      selectedSeriesName,
      filters_startsWith: filters.startsWith,
      browserUrl: window.location.href
    });

    // Use searchParams directly (React Router's state is the source of truth)
    const effectiveSeries = urlSeries;
    const effectiveStartsWith = urlStartsWith;
    // urlGenre already declared above

    syncingRef.current = true;

    // Priority: series takes precedence over startsWith and genre
    // If series is in URL, set it and clear startsWith/genre from both filters and URL
    if (effectiveSeries) {
      // Only update if different to avoid unnecessary re-renders
      if (effectiveSeries !== selectedSeriesName) {
        console.log('Setting selectedSeriesName from URL:', effectiveSeries);
        setSelectedSeriesName(effectiveSeries);
      }
      // Always clear startsWith from filters when a series is selected (but keep in URL)
      setFilters(prev => {
        if (prev.startsWith) {
          console.log('Clearing startsWith from filters because series is selected');
          return { ...prev, startsWith: undefined };
        }
        return prev;
      });
      // Clear selectedGenre when a series is selected (but keep in URL for context)
      if (selectedGenre) {
        console.log('Clearing selectedGenre because series is selected');
        setSelectedGenre(null);
      }
    } else {
      // No series in URL - clear selectedSeriesName if it's set
      if (selectedSeriesName) {
        console.log('Clearing selectedSeriesName because no series in URL');
        setSelectedSeriesName(null);
      }

      // Sync genre from URL if no series is selected
      if (urlGenre && urlGenre !== selectedGenre) {
        console.log('Setting selectedGenre from URL:', urlGenre);
        setSelectedGenre(urlGenre);
      } else if (!urlGenre && selectedGenre) {
        console.log('Clearing selectedGenre because not in URL');
        setSelectedGenre(null);
      }

      // Sync startsWith from URL if no series is selected
      setFilters(prev => {
        if (effectiveStartsWith && effectiveStartsWith !== prev.startsWith) {
          console.log('Setting startsWith from URL:', effectiveStartsWith);
          return { ...prev, startsWith: effectiveStartsWith };
        } else if (!effectiveStartsWith && prev.startsWith) {
          console.log('Clearing startsWith because not in URL');
          return { ...prev, startsWith: undefined };
        }
        return prev;
      });
    }

    // Reset sync flag after state updates (use microtask to ensure it runs after render)
    Promise.resolve().then(() => {
      syncingRef.current = false;
    });
  }, [searchParams, selectedGenre]); // Only depend on searchParams and selectedGenre to prevent loops

  // Sync searchQuery with URL parameter (only when URL changes, not when typing)
  // Use a ref to track the last URL search value to avoid syncing when user is typing
  const lastUrlSearchRef = useRef<string | null>(searchParams.get('search'));

  useEffect(() => {
    // Skip if we're going home (handleGoHome handles state clearing)
    if (goingHomeRef.current) {
      return;
    }

    // Skip if we're currently performing a search (to prevent duplicate calls)
    if (isSearchingRef.current) {
      return;
    }

    const urlSearch = searchParams.get('search');
    const lastUrlSearch = lastUrlSearchRef.current;

    // Only sync from URL to state when URL actually changed (not when searchQuery changes)
    // This allows users to edit the search input without it being overwritten
    if (urlSearch !== lastUrlSearch) {
      lastUrlSearchRef.current = urlSearch;

      // Sync URL value to state (only when URL actually changes)
      if (urlSearch !== null) {
        console.log('Syncing searchQuery from URL:', urlSearch);
        setSearchQuery(urlSearch);
        // Don't set searchTriggeredRef here - let debounce handle it to avoid duplicate calls
        // The debouncedSearchQuery will trigger the fetchRecaps automatically
      } else if (lastUrlSearch !== null) {
        // Search was cleared from URL - clear searchQuery
        // Don't clear lastSearchQueryRef here - let fetch effect handle it to detect "search just cleared"
        console.log('Clearing searchQuery - search removed from URL');
        setSearchQuery('');
        // Keep lastSearchQueryRef.current so fetch effect can detect "search just cleared"
      }
      // Don't clear searchQuery when URL doesn't have search param initially - let user type freely
    }
  }, [searchParams]); // Only react to URL changes, not searchQuery changes

  // Fetch series list when a letter is selected
  useEffect(() => {
    if (filters.startsWith && !selectedSeriesName && !selectedGenre) {
      profiler.start('fetchSeriesList', { startsWith: filters.startsWith, page: 1 });
      setLoadingSeries(true);
      // Clear recaps when switching to series list view
      setRecaps([]);
      // Reset pagination when letter changes
      setLetterSeriesPage(1);
      ApiService.getSeriesList(filters.startsWith, 1, 60)
        .then((response) => {
          profiler.end('fetchSeriesList', {
            seriesCount: response.data.length,
            total: response.total
          });
          setSeriesList(response.data);
          setLetterSeriesPage(1);
          setLetterSeriesHasMore(response.hasMore);
          setLetterSeriesTotal(response.total);
          setLoadingSeries(false);
        })
        .catch((error) => {
          profiler.end('fetchSeriesList', { error: error.message });
          console.error('Error fetching series list:', error);
          setLoadingSeries(false);
        });
    } else if (!filters.startsWith && !selectedSeriesName && !selectedGenre) {
      // Only clear series list if no series is selected (don't clear when a series is selected)
      setSeriesList([]);
      setLetterSeriesPage(1);
      setLetterSeriesHasMore(false);
      setLetterSeriesTotal(0);
    }
  }, [filters.startsWith, selectedSeriesName, selectedGenre]);

  // Fetch series list when a genre is selected
  useEffect(() => {
    const urlGenre = searchParams.get('genre');

    if (urlGenre && !selectedSeriesName) {
      // Update state from URL
      if (urlGenre !== selectedGenre) {
        setSelectedGenre(urlGenre);
        // Reset pagination when genre changes
        setSeriesPage(1);
        setSeriesList([]);
      }

      // Only fetch if we don't have data yet or genre changed
      if (seriesList.length === 0 || urlGenre !== selectedGenre) {
        profiler.start('fetchSeriesByGenre', { genre: urlGenre, page: 1 });
        setLoadingSeries(true);
        // Clear recaps when switching to genre series list view
        setRecaps([]);
        ApiService.getSeriesByGenre(urlGenre, 1, 60)
          .then((response) => {
            profiler.end('fetchSeriesByGenre', {
              seriesCount: response.data.length,
              total: response.total
            });
            setSeriesList(response.data);
            setSeriesPage(1);
            setSeriesHasMore(response.hasMore);
            setSeriesTotal(response.total);
            setLoadingSeries(false);
          })
          .catch((error) => {
            profiler.end('fetchSeriesByGenre', { error: error.message });
            console.error('Error fetching series by genre:', error);
            setLoadingSeries(false);
          });
      }
    } else if (!urlGenre && selectedGenre) {
      // Genre was cleared from URL
      setSelectedGenre(null);
      setSeriesList([]);
      setSeriesPage(1);
      setSeriesHasMore(false);
      setSeriesTotal(0);
    }
  }, [searchParams, selectedSeriesName, selectedGenre]);

  useEffect(() => {
    profiler.start('recapsFetchEffect', {
      filters_startsWith: filters.startsWith,
      selectedSeriesName,
      searchQuery,
      debouncedSearchQuery
    });

    // Skip if we're going home (handleGoHome handles fetching explicitly)
    if (goingHomeRef.current) {
      profiler.end('recapsFetchEffect', { skipped: 'going home' });
      return;
    }

    // Skip if already loading or fetching to prevent multiple simultaneous requests
    if (loading || fetchingRef.current) {
      profiler.end('recapsFetchEffect', { skipped: 'already loading or fetching' });
      return;
    }

    // Skip if we're currently performing a search (to prevent duplicate calls)
    if (isSearchingRef.current) {
      profiler.end('recapsFetchEffect', { skipped: 'search in progress' });
      return;
    }

    // Check URL params early to see if we're showing series list
    const urlStartsWith = searchParams.get('startsWith');
    const urlSeries = searchParams.get('series');
    const urlGenre = searchParams.get('genre');
    const effectiveStartsWith = filters.startsWith || urlStartsWith || undefined;
    const effectiveSeriesName = selectedSeriesName || urlSeries || undefined;
    const effectiveGenre = selectedGenre || urlGenre || undefined;

    // Don't fetch recaps if we're showing the series list (letter or genre selected but no series selected)
    // Check both searchQuery (immediate) and debouncedSearchQuery to handle debounce delay
    // But allow fetching for guests with no filters (show default content)
    if ((effectiveStartsWith || effectiveGenre) && !effectiveSeriesName && !searchQuery && !debouncedSearchQuery && isAuthenticated !== false) {
      profiler.end('recapsFetchEffect', { skipped: 'showing series list only' });
      return; // Just show the series list, don't fetch recaps yet
    }

    // If a letter or genre is selected and search query was cleared, wait for debounce to catch up
    if ((effectiveStartsWith || effectiveGenre) && !effectiveSeriesName && !searchQuery && debouncedSearchQuery) {
      // Search query was cleared but debounce hasn't updated yet, skip this render
      profiler.end('recapsFetchEffect', { skipped: 'waiting for debounce' });
      return;
    }

    // Skip if we're currently performing a search (to prevent duplicate calls)
    // This happens when Enter is pressed - onSearch handler calls fetchRecaps directly
    if (isSearchingRef.current) {
      profiler.end('recapsFetchEffect', { skipped: 'search already in progress' });
      return;
    }

    // Use debouncedSearchQuery for typing
    // Note: Enter key searches are handled directly in onSearch handler, not here
    const effectiveSearchQuery = debouncedSearchQuery;

    // effectiveSeriesName and effectiveStartsWith already computed above

    // Get URL search param
    const urlSearchParam = searchParams.get('search') || '';

    // Skip if this is the same search query we just fetched (prevents duplicate calls after URL sync)
    if (effectiveSearchQuery && effectiveSearchQuery === lastSearchQueryRef.current && urlSearchParam === effectiveSearchQuery) {
      profiler.end('recapsFetchEffect', { skipped: 'duplicate search query' });
      return;
    }

    // Skip if we're transitioning from search to no-search (URL cleared but debounce hasn't caught up)
    // This prevents fetching main page while debounce is still updating
    const hadSearchInUrl = urlSearchParam && urlSearchParam.trim() !== '';
    const hasDebouncedSearch = effectiveSearchQuery && effectiveSearchQuery.trim() !== '';
    const hasSearchQuery = searchQuery && searchQuery.trim() !== '';

    // If URL has no search but we still have search in state/debounce, wait for debounce to clear
    if (!hadSearchInUrl && (hasDebouncedSearch || hasSearchQuery)) {
      profiler.end('recapsFetchEffect', { skipped: 'waiting for debounce after search cleared' });
      return;
    }

    // Skip if we just cleared the search and there's no search in URL (prevents fetching main page after clearing search)
    // Check if search was just cleared (had search before, now empty in URL and state)
    const hadSearchBefore = lastSearchQueryRef.current && lastSearchQueryRef.current.trim() !== '';
    const searchJustCleared = hadSearchBefore && !effectiveSearchQuery && !urlSearchParam && !hasSearchQuery;

    if (searchJustCleared) {
      // Clear the last search query ref to allow future fetches
      // But also set lastFetchParamsRef to prevent duplicate fetches during transition
      lastSearchQueryRef.current = '';

      // Create a key for the cleared state (main page) to prevent duplicates
      const clearedParamsKey = JSON.stringify({
        searchQuery: undefined,
        series_name: undefined,
        channelId: undefined,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
        language: filters.language,
        startsWith: undefined
      });
      lastFetchParamsRef.current = clearedParamsKey;
      lastFetchTimeRef.current = Date.now();

      profiler.end('recapsFetchEffect', { skipped: 'search just cleared' });
      return;
    }

    // Debug logging to see what we're reading from URL
    if (process.env.NODE_ENV !== 'production') {
      console.log('🔍 Frontend: Reading series from URL:', {
        selectedSeriesName: selectedSeriesName,
        urlSeries: urlSeries,
        effectiveSeriesName: effectiveSeriesName,
        searchParamsString: searchParams.toString(),
        windowLocation: window.location.search
      });
    }

    // Clear recaps immediately when filters change (new series selected, search changed, etc.)
    // Skip clearing if we're going home (already cleared in handleGoHome) or already fetching
    if (!goingHomeRef.current && !fetchingRef.current) {
      setRecaps([]);
    }

    const filtersForFetch: FilterOptions = {
      // When a series is selected, use series_name parameter (exact match)
      // Otherwise use searchQuery for text search
      searchQuery: effectiveSeriesName ? undefined : effectiveSearchQuery,
      series_name: effectiveSeriesName || undefined,
      channelId: channelId ? parseInt(channelId, 10) : undefined,
      sortBy: filters.sortBy,
      sortOrder: filters.sortOrder,
      language: filters.language,
      minDuration: filters.minDuration,
      maxDuration: filters.maxDuration,
      startsWith: effectiveSeriesName ? undefined : filters.startsWith, // Don't use startsWith if a series is selected
      genre: effectiveGenre || undefined, // Include genre filter if genre is selected
    };


    // Create a unique key from fetch params to detect duplicate fetches
    const fetchParamsKey = JSON.stringify({
      searchQuery: filtersForFetch.searchQuery,
      series_name: filtersForFetch.series_name,
      channelId: filtersForFetch.channelId,
      sortBy: filtersForFetch.sortBy,
      sortOrder: filtersForFetch.sortOrder,
      language: filtersForFetch.language,
      startsWith: filtersForFetch.startsWith,
      genre: filtersForFetch.genre
    });

    // Skip if we just fetched with the same params (prevents duplicate fetches from multiple effect runs)
    // This is especially important when cache hits complete very quickly (0ms)
    if (lastFetchParamsRef.current === fetchParamsKey) {
      if (fetchingRef.current) {
        profiler.end('recapsFetchEffect', { skipped: 'duplicate fetch params (fetching)' });
        return;
      }
      // Skip if we just fetched these exact params recently (within last 1000ms)
      // This handles cases where fetch completes very quickly from cache (0ms)
      // and the effect runs multiple times due to state updates/debounce
      const timeSinceLastFetch = Date.now() - (lastFetchTimeRef.current || 0);
      if (timeSinceLastFetch < 1000) {
        profiler.end('recapsFetchEffect', { skipped: 'duplicate fetch params (recent cache hit)' });
        return;
      }
    }

    // Debug logging
    if (process.env.NODE_ENV !== 'production' && effectiveSeriesName) {
      console.log('🔍 Frontend: Sending series_name:', {
        selectedSeriesName: selectedSeriesName,
        urlSeries: urlSeries,
        effectiveSeriesName: effectiveSeriesName,
        filtersForFetch: filtersForFetch
      });
    }

    profiler.start('fetchRecaps', { filters: filtersForFetch });
    // Don't scroll to top if we're loading more series
    if (!isLoadingMoreSeriesRef.current) {
      window.scrollTo(0, 0);
    }

    // Update last search query ref to prevent duplicate fetches
    if (effectiveSearchQuery) {
      lastSearchQueryRef.current = effectiveSearchQuery;
    } else {
      lastSearchQueryRef.current = '';
    }

    // Update last fetch params and time BEFORE fetching to prevent rapid duplicates
    // This is critical for cache hits that complete in < 10ms
    lastFetchParamsRef.current = fetchParamsKey;
    lastFetchTimeRef.current = Date.now();

    // Mark as fetching to prevent concurrent calls
    fetchingRef.current = true;

    fetchRecaps(filtersForFetch, 1).finally(() => {
      // Reset fetching flag after fetch completes
      // Add delay to catch rapid re-runs that happen immediately after cache hits (0ms)
      // Longer delay for cache hits to prevent duplicates from multiple effect runs
      setTimeout(() => {
        fetchingRef.current = false;
      }, 300); // Delay to catch rapid duplicates from cache hits (longer for multiple effect runs)
    });

    // Note: fetchRecaps handles its own profiling internally
    profiler.end('recapsFetchEffect', { completed: true });

    // Save search to history when it's actually performed (only if it's different from last saved)
    if (effectiveSearchQuery && effectiveSearchQuery.trim() && effectiveSearchQuery !== lastSavedSearchRef.current && !selectedSeriesName) {
      lastSavedSearchRef.current = effectiveSearchQuery;
      saveSearchToHistory(effectiveSearchQuery);
    }
  }, [debouncedSearchQuery, channelId, filters.sortBy, filters.sortOrder, filters.language, filters.minDuration, filters.maxDuration, filters.startsWith, selectedSeriesName, selectedGenre, saveSearchToHistory, fetchRecaps, setRecaps]);

  const { selectedRecap, isModalOpen, openModal, closeModal } = useModal();

  const handleRecapClick = (recap: ManhwaRecap) => {
    openModal(recap);
  };

  const handleCloseModal = () => {
    closeModal();
  };

  const handleLoadMore = () => {
    loadMore();
  };

  const handleGoHome = () => {
    // Prevent double-clicks or rapid calls
    const now = Date.now();
    const timeSinceLastClick = now - lastGoHomeClickTimeRef.current;

    if (goingHomeRef.current || timeSinceLastClick < 500) {
      console.log('handleGoHome: Already going home or recent click, skipping', {
        goingHome: goingHomeRef.current,
        timeSinceLastClick
      });
      return;
    }

    lastGoHomeClickTimeRef.current = now;
    goingHomeRef.current = true;

    // Clear all URL parameters first, before clearing state
    // This ensures effects read cleared URL params
    setSearchParams({});
    navigate('/', { replace: true });

    // Clear all state - this will trigger effects to run with cleared values
    setSearchQuery('');
    searchTriggeredRef.current = false; // Reset trigger flag
    lastSearchQueryRef.current = ''; // Clear last search query ref
    const clearedFilters: FilterOptions = {
      language: 'en',
      sortBy: 'published_at',
      sortOrder: 'desc',
      searchQuery: '',
      startsWith: undefined
    };
    setFilters(clearedFilters);
    setSelectedSeriesName(null);
    setSelectedGenre(null);
    setSeriesList([]);
    setRecaps([]); // Clear recaps immediately

    // Create fetch params key for cleared state to prevent duplicate fetches
    const clearedParamsKey = JSON.stringify({
      searchQuery: undefined,
      series_name: undefined,
      channelId: undefined,
      sortBy: 'published_at',
      sortOrder: 'desc',
      language: 'en',
      startsWith: undefined
    });
    lastFetchParamsRef.current = clearedParamsKey;
    lastFetchTimeRef.current = Date.now();

    // Explicitly fetch recaps to ensure page loads
    // Use setTimeout to ensure state updates have been processed
    setTimeout(() => {
      fetchingRef.current = true;
      fetchRecaps(clearedFilters, 1).finally(() => {
        fetchingRef.current = false;
        // Reset going home flag after fetch completes (with delay to prevent effect from running)
        setTimeout(() => {
          goingHomeRef.current = false;
        }, 500); // Longer delay to prevent effect from running after going home
      });
    }, 0);
  };

  const handleSeriesClick = (seriesName: string) => {
    // Clear previous recaps immediately when selecting a new series
    setRecaps([]);
    setSelectedSeriesName(seriesName);
    // Clear genre selection when selecting a series
    if (selectedGenre) {
      setSelectedGenre(null);
    }
    // Clear startsWith filter when selecting a series
    if (filters.startsWith) {
      setFilters(prev => ({ ...prev, startsWith: undefined }));
    }
    setSearchQuery(''); // Clear search query when selecting a series
    searchTriggeredRef.current = false; // Reset trigger flag
    lastSearchQueryRef.current = ''; // Clear last search query to ensure fetch happens

    // Update URL parameters - add series, remove genre/startsWith since series takes precedence
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('series', seriesName);
    // Remove genre and startsWith from URL since series takes precedence
    newSearchParams.delete('genre');
    newSearchParams.delete('startsWith');
    setSearchParams(newSearchParams);

    // Clear startsWith from filters (so it doesn't affect the query), but keep it in URL
    if (filters.startsWith) {
      setFilters({ ...filters, startsWith: undefined });
    }

    // Explicitly trigger fetchRecaps to ensure recaps are loaded
    // Use setTimeout to ensure state updates have been processed
    setTimeout(() => {
      const filtersForFetch: FilterOptions = {
        ...filters,
        startsWith: undefined, // Don't use startsWith filter when a series is selected
        language: filters.language || 'en',
        sortBy: filters.sortBy || 'published_at',
        sortOrder: filters.sortOrder || 'desc',
        searchQuery: '' // Clear search query
      };

      fetchingRef.current = true;
      fetchRecaps({ ...filtersForFetch, series_name: seriesName }, 1).finally(() => {
        fetchingRef.current = false;
      });
    }, 0);
  };

  const handleCloseSeriesList = () => {
    setFilters({ ...filters, startsWith: undefined });
    setSelectedSeriesName(null);
    setSeriesList([]);
    // Clear series from URL
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('series');
    setSearchParams(newSearchParams);
  };

  // Don't block rendering on auth check - show UI immediately
  // Auth check happens in background and updates UI when complete
  return (
    <div className="bg-white dark:bg-gray-900 text-gray-900 dark:text-white min-h-screen font-sans transition-colors duration-200">
      {/* Header */}
        <header className="bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-40 transition-colors duration-200">
          <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex justify-between items-center">
              {/* Left side: Logo and Navigation */}
              <div className="flex items-center space-x-2 md:space-x-4 lg:space-x-6">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleGoHome();
                  }}
                  className="flex items-center space-x-1 md:space-x-2"
                >
                  <div className="p-1.5 md:p-2 bg-purple-600 rounded-lg">
                    <BookOpen className="w-5 h-5 md:w-6 md:h-6 text-white" />
                  </div>
                  <span className="text-base md:text-lg lg:text-xl font-bold text-gray-900 dark:text-white">ManhwaRecap</span>
                </button>
                <Navigation />
              </div>

              {/* Right side: User actions */}
              <div className="flex items-center space-x-1 md:space-x-2 lg:space-x-4">
                <ThemeToggle />
                {isAuthenticated && user ? (
                  <div className="hidden sm:flex items-center space-x-4">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Welcome, {user.username}</span>
                    <button
                      onClick={handleLogout}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
                    >
                      Logout
                    </button>
                  </div>
                ) : needsEmailVerification ? (
                  <div className="hidden sm:flex items-center space-x-4">
                    <span className="text-sm font-medium text-orange-600 dark:text-orange-400">
                      Verify email: {verificationDaysLeft > 0 ? `${verificationDaysLeft} days left` : 'Overdue'}
                    </span>
                    <Link to="/email-verification" className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-md hover:bg-orange-700">
                      Verify Email
                    </Link>
                  </div>
                ) : (
                  <div className="hidden sm:flex items-center space-x-2">
                    <Link to="/login" className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700">Login</Link>
                    <Link to="/register" className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 dark:text-white dark:bg-gray-600 dark:hover:bg-gray-500">Register</Link>
                  </div>
                )}

              </div>
            </div>
          </div>
        </header>

      {/* Channel Header - Only show when viewing a specific channel */}
      {channelId && recaps.length > 0 && (
        <ChannelHeader
          channelId={parseInt(channelId, 10)}
          channelName={recaps[0].channel_name}
          language={filters.language}
        />
      )}

      {/* Email Verification Banner */}
      {isAuthenticated && needsEmailVerification && verificationDaysLeft <= 7 && (
        <div className="bg-orange-100 dark:bg-orange-900/20 border-l-4 border-orange-500 p-4 mx-4 sm:mx-6 lg:mx-8 mt-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-orange-800 dark:text-orange-200">
                  {verificationDaysLeft > 0 ? (
                    <>Please verify your email within <strong>{verificationDaysLeft} days</strong> to keep accessing all features.</>
                  ) : (
                    <>Your account requires email verification. Please verify your email to continue.</>
                  )}
                </p>
              </div>
            </div>
            <div className="ml-auto pl-3">
              <div className="-mx-1.5 -my-1.5">
                <Link
                  to="/email-verification"
                  className="inline-flex bg-orange-100 dark:bg-orange-900/20 px-2 py-1.5 rounded-md text-sm font-medium text-orange-800 dark:text-orange-200 hover:bg-orange-200 dark:hover:bg-orange-900/40"
                >
                  Verify Now
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Verification Required Gate */}
      {!canAccessContent && !authLoading && isAuthenticated && (
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 text-center">
            <div className="mb-6">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-orange-100 dark:bg-orange-900/20">
                <svg className="h-6 w-6 text-orange-600 dark:text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h2 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
                {verificationDaysLeft > 0 ? 'Email Verification Required' : 'Email Verification Overdue'}
              </h2>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                {verificationDaysLeft > 0
                  ? `Please verify your email within ${verificationDaysLeft} days to continue accessing manhwarecap.com.`
                  : 'Your account requires email verification to continue using the service.'
                }
              </p>
            </div>
            <div className="space-y-3">
              <Link
                to="/email-verification"
                className="w-full inline-flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
              >
                Verify Email
              </Link>
              <button
                onClick={handleLogout}
                className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      {canAccessContent && (
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="space-y-4">
          {/* Search Bar - Full width */}
          <SearchBar
            value={searchQuery}
            onChange={(value) => {
              setSearchQuery(value);
              // Reset trigger flag when user types (not pressing Enter)
              searchTriggeredRef.current = false;
              // Clear startsWith and genre filters when user starts typing in search bar
              if (value.trim() && (filters.startsWith || selectedGenre)) {
                setFilters({ ...filters, startsWith: undefined });
                setSelectedSeriesName(null);
                // Update URL parameter
                const newSearchParams = new URLSearchParams(searchParams);
                newSearchParams.delete('startsWith');
                newSearchParams.delete('genre');
                setSearchParams(newSearchParams);
              }
            }}
            onSearch={(query) => {
              // Prevent duplicate calls
              if (isSearchingRef.current) {
                return;
              }

              isSearchingRef.current = true;

              // If we're on a channel page, navigate to main page with search query
              if (channelId) {
                navigate(`/?search=${encodeURIComponent(query)}`);
                isSearchingRef.current = false;
                return;
              }

              // For main page or A-Z pages, update URL with search parameter
              const newSearchParams = new URLSearchParams(searchParams);
              newSearchParams.set('search', query);
              // Clear series, startsWith, and genre when searching
              newSearchParams.delete('series');
              newSearchParams.delete('startsWith');
              newSearchParams.delete('genre');

              // Update search query state
              setSearchQuery(query);

              // Clear recaps immediately when search is triggered (Enter pressed)
              setRecaps([]);

              // Trigger search immediately when Enter is pressed (don't wait for debounce)
              const filtersForFetch: FilterOptions = {
                searchQuery: query,
                channelId: channelId ? parseInt(channelId, 10) : undefined,
                sortBy: filters.sortBy,
                sortOrder: filters.sortOrder,
                language: filters.language,
                minDuration: filters.minDuration,
                maxDuration: filters.maxDuration,
                startsWith: undefined, // Clear startsWith when searching
              };
              window.scrollTo(0, 0);

              // Update last search query ref before fetching
              lastSearchQueryRef.current = query;

              // Update URL after starting the search to prevent URL sync from triggering another call
              setSearchParams(newSearchParams);

              fetchRecaps(filtersForFetch, 1).finally(() => {
                // Reset flag after search completes (wait longer than debounce delay to prevent duplicate calls)
                setTimeout(() => {
                  isSearchingRef.current = false;
                }, 600); // 600ms > 300ms debounce delay + buffer
              });
            }}
            placeholder="Search by series name, title, description, channel, or tags..."
            isLoading={loading}
          />

          {/* Filter Controls - Compact mobile layout */}
          <div className="flex flex-wrap gap-2 sm:gap-3 mt-3">
            {/* Language Filter */}
            <div className="flex items-center gap-1">
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">Lang:</label>
              <select
                value={filters.language || ''}
                onChange={(e) => {
                  const newLanguage = e.target.value;
                  setFilters({ ...filters, language: newLanguage || undefined });
                  // Update URL
                  const newSearchParams = new URLSearchParams(searchParams);
                  if (newLanguage) {
                    newSearchParams.set('language', newLanguage);
                  } else {
                    newSearchParams.delete('language');
                  }
                  setSearchParams(newSearchParams);
                }}
                className="px-2 py-1 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-white text-xs"
              >
                <option value="">All</option>
                <option value="de">DE</option>
                <option value="en">EN</option>
                <option value="es">ES</option>
                <option value="fil">FIL</option>
                <option value="fr">FR</option>
                <option value="id">ID</option>
                <option value="it">IT</option>
                <option value="lus">LUS</option>
                <option value="my">MY</option>
                <option value="pt">PT</option>
                <option value="ru">RU</option>
                <option value="th">TH</option>
                <option value="tl">TL</option>
                <option value="vi">VI</option>
                <option value="zh">ZH</option>
              </select>
            </div>

            {/* Sort By */}
            <div className="flex items-center gap-1">
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">Sort:</label>
              <select
                value={filters.sortBy || 'published_at'}
                onChange={(e) => {
                  const newSortBy = e.target.value;
                  setFilters({ ...filters, sortBy: newSortBy });
                  // Update URL
                  const newSearchParams = new URLSearchParams(searchParams);
                  newSearchParams.set('sortBy', newSortBy);
                  setSearchParams(newSearchParams);
                }}
                className="px-2 py-1 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-white text-xs"
              >
                <option value="published_at">Date</option>
                <option value="views">Views</option>
                <option value="duration">Duration</option>
                <option value="rating">Rating</option>
                <option value="like_count">Likes</option>
              </select>
            </div>

            {/* Sort Order */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => {
                  const newSortOrder = filters.sortOrder === 'asc' ? 'desc' : 'asc';
                  setFilters({ ...filters, sortOrder: newSortOrder });
                  // Update URL
                  const newSearchParams = new URLSearchParams(searchParams);
                  newSearchParams.set('sortOrder', newSortOrder);
                  setSearchParams(newSearchParams);
                }}
                className="px-2 py-1 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-white text-xs hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex items-center justify-center min-w-[28px]"
                title={`Sort order: ${filters.sortOrder === 'asc' ? 'Ascending' : 'Descending'}`}
              >
                {filters.sortOrder === 'asc' ? '↑' : '↓'}
              </button>
            </div>

            {/* Duration Filter - Only show when "Sort by Duration" is selected */}
            {filters.sortBy === 'duration' && (
              <div className="flex items-center gap-1">
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">Dur:</label>
                <select
                  value={getSelectedDurationValue()}
                  onChange={(e) => {
                    const val = e.target.value;
                    let minDuration: number | undefined = undefined;
                    let maxDuration: number | undefined = undefined;
                    if (val === 'under1h') maxDuration = 3600;
                    else if (val === '1to2h') { minDuration = 3600; maxDuration = 7200; }
                    else if (val === '2to4h') { minDuration = 7200; maxDuration = 14400; }
                    else if (val === 'over4h') minDuration = 14400;

                    // When a duration filter is applied, automatically set sort order to ASC
                    // so users see shortest durations first (most relevant)
                    const newSortOrder = val ? 'asc' : filters.sortOrder;

                    setFilters({
                      ...filters,
                      minDuration,
                      maxDuration,
                      sortOrder: newSortOrder
                    });

                    // Update URL
                    const newSearchParams = new URLSearchParams(searchParams);
                    if (minDuration !== undefined) newSearchParams.set('minDuration', minDuration.toString());
                    else newSearchParams.delete('minDuration');
                    if (maxDuration !== undefined) newSearchParams.set('maxDuration', maxDuration.toString());
                    else newSearchParams.delete('maxDuration');
                    if (newSortOrder !== 'desc') newSearchParams.set('sortOrder', newSortOrder);
                    else newSearchParams.delete('sortOrder');
                    setSearchParams(newSearchParams);
                  }}
                  className="px-2 py-1 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-gray-900 dark:text-white text-xs"
                >
                  <option value="">All</option>
                  <option value="under1h">&lt;1h</option>
                  <option value="1to2h">1-2h</option>
                  <option value="2to4h">2-4h</option>
                  <option value="over4h">&gt;4h</option>
                </select>
              </div>
            )}
          </div>

          {/* A-Z Toggle Button - below search/filter row */}
          <div className="flex justify-start">
            <button
              onClick={() => setShowAlphabeticalFilter(!showAlphabeticalFilter)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                showAlphabeticalFilter
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              {showAlphabeticalFilter ? 'Hide A-Z Filter' : 'Show A-Z Filter'}
            </button>
          </div>

          {/* Alphabetical/Numberical Filter - Only show when toggled */}
          {showAlphabeticalFilter && (
            <AlphabeticalFilter
            startsWith={filters.startsWith}
            onStartsWithChange={(char) => {
              console.log('onStartsWithChange called with:', char);
              console.log('Current URL params:', searchParams.toString());

              // Mark that we're clearing series to prevent sync useEffect from re-adding it
              clearingSeriesRef.current = true;

              // Clear series selection FIRST before updating URL
              setSelectedSeriesName(null);

              // Update URL - remove series parameter
              const newSearchParams = new URLSearchParams(searchParams);
              if (char) {
                newSearchParams.set('startsWith', char);
                newSearchParams.delete('search'); // Remove search from URL when selecting a letter
                newSearchParams.delete('series'); // Remove series from URL when selecting a letter
              } else {
                newSearchParams.delete('startsWith');
                newSearchParams.delete('series');
              }

              console.log('New URL params:', newSearchParams.toString());
              console.log('Has series?', newSearchParams.has('series'));

              // Use navigate to immediately update URL (bypasses React batching)
              // Don't call setSearchParams - navigate will update searchParams automatically
              const newUrl = `?${newSearchParams.toString()}`;
              console.log('Navigating to:', newUrl);
              navigate(newUrl, { replace: true });

              // Verify URL was updated after a brief delay
              setTimeout(() => {
                console.log('URL after navigate:', window.location.href);
                console.log('URL search params:', window.location.search);
              }, 50);

              // Then update filters
              setFilters({ ...filters, startsWith: char });
              setSearchQuery(''); // Clear search query when clicking a letter
              searchTriggeredRef.current = false; // Reset trigger flag

              // Reset the clearing flag after a delay (long enough for URL update to complete)
              setTimeout(() => {
                clearingSeriesRef.current = false;
              }, 500);
            }}
          />
          )}


          {/* Series List - Show when a letter is selected */}
          {filters.startsWith && !selectedSeriesName && !selectedGenre && (
            <SeriesList
              series={seriesList}
              loading={loadingSeries}
              selectedLetter={filters.startsWith}
              total={letterSeriesTotal}
              hasMore={letterSeriesHasMore}
              onSeriesClick={handleSeriesClick}
              onLoadMore={async () => {
                if (loadingSeries || !letterSeriesHasMore) return;
                const nextPage = letterSeriesPage + 1;

                // Store current scroll position before loading
                const scrollPosition = window.scrollY;

                // Set flag to prevent scroll-to-top in fetchRecaps
                isLoadingMoreSeriesRef.current = true;
                setLoadingSeries(true);
                try {
                  const response = await ApiService.getSeriesList(filters.startsWith, nextPage, 60);
                  setSeriesList(prev => [...prev, ...response.data]);
                  setLetterSeriesPage(nextPage);
                  setLetterSeriesHasMore(response.hasMore);

                  // After DOM updates, restore scroll position
                  // Use double requestAnimationFrame to ensure DOM has fully updated with new content
                  requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                      // Restore the exact scroll position - scrollbar will naturally grow as content is added
                      window.scrollTo(0, scrollPosition);
                      // Clear the flag after scroll restoration
                      isLoadingMoreSeriesRef.current = false;
                    });
                  });
                } catch (error) {
                  console.error('Error loading more series:', error);
                  isLoadingMoreSeriesRef.current = false;
                } finally {
                  setLoadingSeries(false);
                }
              }}
              onClose={() => {
                setFilters({ ...filters, startsWith: undefined });
                setSelectedGenre(undefined);
                setSeriesList([]);
              }}
            />
          )}

          {/* Series List - Show when a genre is selected */}
          {selectedGenre && !selectedSeriesName && (
            <SeriesList
              series={seriesList}
              loading={loadingSeries}
              selectedGenre={selectedGenre}
              total={seriesTotal}
              hasMore={seriesHasMore}
              onSeriesClick={handleSeriesClick}
              onLoadMore={async () => {
                if (loadingSeries || !seriesHasMore) return;

                const nextPage = seriesPage + 1;

                // Store current scroll position before loading
                const scrollPosition = window.scrollY;

                // Set flag to prevent scroll-to-top in fetchRecaps
                isLoadingMoreSeriesRef.current = true;
                setLoadingSeries(true);
                try {
                  const response = await ApiService.getSeriesByGenre(selectedGenre, nextPage, 60);
                  setSeriesList(prev => [...prev, ...response.data]);
                  setSeriesPage(nextPage);
                  setSeriesHasMore(response.hasMore);

                  // After DOM updates, restore scroll position
                  // Use double requestAnimationFrame to ensure DOM has fully updated with new content
                  requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                      // Restore the exact scroll position - scrollbar will naturally grow as content is added
                      window.scrollTo(0, scrollPosition);
                      // Clear the flag after scroll restoration
                      isLoadingMoreSeriesRef.current = false;
                    });
                  });
                } catch (error) {
                  console.error('Error loading more series:', error);
                  isLoadingMoreSeriesRef.current = false;
                } finally {
                  setLoadingSeries(false);
                }
              }}
              onClose={() => {
                setSelectedGenre(null);
                setSeriesList([]);
                setSeriesPage(1);
                setSeriesHasMore(false);
                setSeriesTotal(0);
                const newSearchParams = new URLSearchParams(searchParams);
                newSearchParams.delete('genre');
                setSearchParams(newSearchParams);
              }}
            />
          )}

          {/* Show selected series name */}
          {selectedSeriesName && (
            <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Showing recaps for:</span>
                  <span className="font-semibold text-purple-700 dark:text-purple-300">{selectedSeriesName}</span>
                </div>
                <button
                  onClick={() => {
                    // Clear series selection and search query
                    setSelectedSeriesName(null);
                    setSearchQuery('');
                    lastSearchQueryRef.current = ''; // Clear last search query ref
                    // Clear series from URL
                    const newSearchParams = new URLSearchParams(searchParams);
                    newSearchParams.delete('series');
                    setSearchParams(newSearchParams);
                  }}
                  className="text-sm text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 underline"
                >
                  Clear
                </button>
              </div>
            </div>
          )}

          {/* Recap Count Display - Hide when showing series list (letter/genre selected but no series selected) */}
          {!loading && ((!filters.startsWith && !selectedGenre) || selectedSeriesName || (debouncedSearchQuery && !filters.startsWith && !selectedGenre)) && (
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <div className="flex items-center space-x-2">
                <span className="font-medium">
                  {total} recap{total !== 1 ? 's' : ''} found
                </span>
                {(selectedSeriesName || debouncedSearchQuery || filters.language) && (
                  <span className="text-gray-500 dark:text-gray-500">
                    {selectedSeriesName ? `• Series: "${selectedSeriesName}"` : ''}
                    {selectedSeriesName && (debouncedSearchQuery || filters.language) ? ' • ' : ''}
                    {!selectedSeriesName && debouncedSearchQuery ? `• Searching for "${debouncedSearchQuery}"` : ''}
                    {!selectedSeriesName && debouncedSearchQuery && filters.language ? ' • ' : ''}
                    {filters.language ? `Language: ${filters.language}` : ''}
                  </span>
                )}
              </div>
            </div>
          )}

        </div>
      </div>
      )}

      {/* Results - Only show when not in series list mode (letter/genre selected but no series selected) */}


      {/* Main Results - Show on main page or when series is selected */}
      {canAccessContent && !(filters.startsWith || selectedGenre) && (
        <>
          <main className="mx-auto px-4 sm:px-6 lg:px-8 pb-12">
            {error ? (
            <div className="text-center py-12">
              <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">Error loading recaps</h3>
              <p className="text-gray-600 dark:text-gray-500 mb-4">{error}</p>
              <button
                onClick={() => fetchRecaps(filters)}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                Try Again
              </button>
            </div>
          ) : loading && recaps.length === 0 ? (
            <LoadingSpinner />
          ) : recaps.length === 0 && !loading ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🎬</div>
              <h3 className="text-2xl font-bold text-gray-700 dark:text-gray-300 mb-2">Welcome to manhwarecap.com!</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Discover amazing manhwa recaps from the best creators on YouTube.
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-500">
                Use the alphabetical filter above to browse by series, or start typing to search.
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {recaps.map(recap => (
                  <RecapCard
                    key={recap.id}
                    recap={recap}
                    onClick={() => handleRecapClick(recap)}
                  />
                ))}
              </div>

              {/* Load More Button */}
              {hasMore && (
                <div className="text-center mt-8">
                  <button
                    onClick={handleLoadMore}
                    disabled={loading}
                    className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                  >
                    {loading ? 'Loading...' : 'Load More'}
                  </button>
                </div>
              )}
            </>
          )}
          </main>
        </>
      )}

      {/* PWA Install Prompt */}
      <PWAInstallPrompt />

      {/* Modal - Always render outside content restrictions */}
      <RecapModal
        recap={selectedRecap}
        isOpen={isModalOpen}
        onClose={closeModal}
      />
    </div>
  );
}

export default App;